import React, {useEffect, useState} from 'react';
import * as dayjs from 'dayjs';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import addMonths from 'date-fns/addMonths';
import subMonths from 'date-fns/subMonths';
import subYears from 'date-fns/subYears';
import {DateRangePicker} from 'rsuite';
import {ArrowDownRight, ArrowUpRight} from 'react-feather';

import {get} from '../services/ApiService';
import {Util} from '../util/Util';
import {Avatar} from '../components/Avatar';
import {useLayout} from '../components/Layout';
import Grid from '../components/Core/Grid';
import Overlay from '../components/Overlay';
import TimeSeriesChart from '../components/Dashboard/TimeSeriesChart';
import LineChart from '../components/Dashboard/LineChart';
import TableChart from '../components/Dashboard/TableChart';

import './Dashboard2.scss';

const ENDPOINT = '/?route=home/grid&action=indicadores';
const ENDPOINT_DATA = '/?route=home/grid&action=dashboard2Data';

const defaultDate = [startOfMonth(new Date()), new Date()];

const predefinedRanges = [
    {label: 'Este mês', value: [startOfMonth(new Date()), new Date()]},
    {label: 'Um mês atrás', value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))]},
    {label: 'Dois meses atrás', value: [startOfMonth(addMonths(new Date(), -2)), endOfMonth(addMonths(new Date(), -2))]},
    {label: 'Três meses atrás', value: [startOfMonth(addMonths(new Date(), -3)), endOfMonth(addMonths(new Date(), -3))]},
    {label: 'Este ano', value: [new Date(new Date().getFullYear(), 0, 1), new Date()]},
    {label: 'Ano passado', value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear(), 0, 0)]},
    {label: 'Todo o período', value: [new Date(new Date().getFullYear() - 5, 0, 1), new Date()]},
];

const COMPARACOES = [
    {key: 'dias', label: 'Dias anteriores'},
    {key: 'mes', label: 'Mês anterior'},
    {key: 'ano', label: 'Ano anterior'},
];

const KPIS = [
    {key: 'projetos', label: 'Projetos', fmt: 'int'},
    {key: 'placas', label: 'Placas', fmt: 'int'},
    {key: 'potencia', label: 'Potência', fmt: 'kwp'},
];

function fmt(value, tipo) {
    if (value === null || value === undefined) return '-';
    if (tipo === 'moeda') return Util.moeda(value);
    if (tipo === 'kwp') return `${Math.floor(value)} kWp`;
    return Math.round(value);
}

function fmtInversor(inversor) {
    if (inversor === null || inversor === undefined || inversor === '') return 'Sem inversor';
    const n = parseFloat(inversor);
    return `${Number.isInteger(n) ? n : n.toFixed(1)} kW`;
}

function delta(cur, prev) {
    const c = Number(cur) || 0;
    const p = Number(prev) || 0;
    if (p !== 0) return ((c - p) / Math.abs(p)) * 100;
    if (c !== 0) return 100;
    return null;
}

function formatRange(range) {
    const value = (range && range[0] && range[1]) ? range : defaultDate;
    return [
        dayjs(value[0]).format('YYYY-MM-DD HH:mm:ss'),
        dayjs(value[1]).format('YYYY-MM-DD HH:mm:ss'),
    ];
}

function previousRange(range, modo) {
    const value = (range && range[0] && range[1]) ? range : defaultDate;
    if (modo === 'mes') return [subMonths(value[0], 1), subMonths(value[1], 1)];
    if (modo === 'ano') return [subYears(value[0], 1), subYears(value[1], 1)];
    // 'dias': mesma quantidade de dias imediatamente antes
    const inicio = value[0].getTime();
    const duracao = value[1].getTime() - inicio;
    return [new Date(inicio - duracao - 1), new Date(inicio - 1)];
}

function indexByTitle(list) {
    const map = {};
    (list || []).forEach((item) => { map[item.title] = item; });
    return map;
}

const Delta = ({cur, prev, neutro}) => {
    const pct = delta(cur, prev);
    let cls = 'flat';
    if (pct !== null && Math.abs(pct) >= 0.5 && !neutro) cls = pct > 0 ? 'up' : 'down';
    return (
        <span className={`dash2-delta ${cls}`}>
            {pct === null && 'sem base anterior'}
            {pct !== null && (
                <>
                    {pct > 0 ? <ArrowUpRight size={13}/> : (pct < 0 ? <ArrowDownRight size={13}/> : null)}
                    {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                </>
            )}
        </span>
    );
};

const Card = ({title, className, children}) => (
    <div className={`dash2-card ${className || ''}`}>
        <div className="dash2-card-title">{title}</div>
        <div className="dash2-card-body">{children}</div>
    </div>
);

const Funil = ({etapas, onEtapa}) => {
    if (!etapas || !etapas.length) return <div className="dash2-vazio">Sem dados no período</div>;

    // Orçamentos é a base (100%); Projetos é % dos orçamentos; o restante é % dos projetos.
    const orcamentos = etapas[0]?.total || 0;
    const projetos = etapas[1]?.total || 0;
    const percentual = (etapa, i) => {
        if (i === 0) return 100;
        if (i === 1) return orcamentos ? (etapa.total / orcamentos) * 100 : 0;
        return projetos ? (etapa.total / projetos) * 100 : 0;
    };
    const fmtPct = (v) => `${v % 1 === 0 ? v : v.toFixed(1)}%`;

    return (
        <div className="dash2-funil">
            {etapas.map((etapa, i) => {
                const pct = percentual(etapa, i);
                return (
                    <React.Fragment key={etapa.label}>
                        <div
                            className={`dash2-funil-etapa etapa-${i} clicavel`}
                            onClick={() => onEtapa && onEtapa(etapa)}
                            title="Abrir lista filtrada"
                        >
                            <div className="dash2-funil-valor">{etapa.total}</div>
                            <div className="dash2-funil-label">{etapa.label}</div>
                            <div className="dash2-funil-bar">
                                <div style={{width: `${Math.min(100, pct)}%`}}/>
                            </div>
                            <div className="dash2-funil-pct">{fmtPct(pct)}</div>
                        </div>
                        {i < etapas.length - 1 && <div className="dash2-funil-seta">›</div>}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default function Dashboard2() {
    const layout = useLayout();
    const [isLoading, setIsLoading] = useState(true);
    const [dateRange, setDateRange] = useState(defaultDate);
    const [comparacao, setComparacao] = useState('dias');
    const [indicadores, setIndicadores] = useState({});
    const [dados, setDados] = useState(null);
    const [dadosAnt, setDadosAnt] = useState(null);
    const [compRange, setCompRange] = useState(null);
    const [periodoRange, setPeriodoRange] = useState(defaultDate);

    const abrirGrid = (nome, router, initialFilter) => {
        if (!layout) return;
        layout.openMenu({
            name: nome,
            icon: null,
            component: Grid,
            props: {router, initialFilter: initialFilter || [], selectionMode: 'none'},
        }, true);
    };

    const abrirEtapaFunil = (etapa) => {
        if (etapa.tipo === 'orcamento') {
            abrirGrid('Orçamentos', 'orcamento');
        } else if (etapa.situacao == null) {
            abrirGrid('Projetos', 'contrato');
        } else {
            abrirGrid(`Projetos: ${etapa.label}`, 'contrato', [
                {name: 'cont_situacao', operator: 'eq', value: String(etapa.situacao)},
            ]);
        }
    };

    const abrirVendedor = (v) => {
        if (!v.id) return;
        abrirGrid(`Projetos: ${v.nome}`, 'contrato', [
            {name: 'ven_id', operator: 'eq', value: String(v.id)},
        ]);
    };

    const loadData = (range, modo) => {
        setIsLoading(true);
        setPeriodoRange(range);
        const prevObj = previousRange(range, modo);
        setCompRange(prevObj);
        const cur = formatRange(range);
        const prev = formatRange(prevObj);
        return Promise.all([
            get(`${ENDPOINT}&dateRange=${cur}`),
            get(`${ENDPOINT_DATA}&dateRange=${cur}`),
            get(`${ENDPOINT_DATA}&dateRange=${prev}`),
        ])
            .then(([resInd, resData, resAnt]) => {
                if (resInd.status === 200) setIndicadores(indexByTitle(resInd.data));
                if (resData.status === 200) setDados(resData.data);
                if (resAnt.status === 200) setDadosAnt(resAnt.data);
            })
            .finally(() => setIsLoading(false));
    };

    const trocarComparacao = (modo) => {
        setComparacao(modo);
        loadData(dateRange, modo);
    };

    useEffect(() => {
        loadData(dateRange, comparacao);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const grupo = (title) => indicadores[title];
    const t = dados?.totais || {};
    const tAnt = dadosAnt?.totais || {};
    const periodoLabel = `${dayjs(periodoRange[0]).format('DD/MM/YYYY')} a ${dayjs(periodoRange[1]).format('DD/MM/YYYY')}`;
    const compLabel = compRange ? `${dayjs(compRange[0]).format('DD/MM/YYYY')} a ${dayjs(compRange[1]).format('DD/MM/YYYY')}` : '';

    const vendedores = dados?.vendedores || [];
    const totalVend = vendedores.reduce((a, v) => ({
        total: a.total + (Number(v.total) || 0),
        equipamentos: a.equipamentos + (Number(v.equipamentos) || 0),
        maoObra: a.maoObra + (Number(v.mao_obra) || 0),
        quantidade: a.quantidade + (Number(v.quantidade) || 0),
    }), {total: 0, equipamentos: 0, maoObra: 0, quantidade: 0});

    return (
        <div className="dashboard2">
            <header className="dash2-hero">
                <div className="dash2-hero-main">
                    <span className="dash2-hero-eyebrow">Vendas no período · {periodoLabel}</span>
                    <div className="dash2-hero-valor">{fmt(t.vendas, 'moeda')}</div>
                    <Delta cur={t.vendas} prev={tAnt.vendas}/>
                </div>
                <div className="dash2-hero-side">
                    <div className="dash2-hero-mini">
                        <div className="dash2-hero-mini-label">Equipamentos</div>
                        <div className="dash2-hero-mini-valor">{fmt(t.equipamentos, 'moeda')}</div>
                        <Delta cur={t.equipamentos} prev={tAnt.equipamentos}/>
                    </div>
                    <div className="dash2-hero-mini">
                        <div className="dash2-hero-mini-label">Mão de obra</div>
                        <div className="dash2-hero-mini-valor">{fmt(t.maoObra, 'moeda')}</div>
                        <Delta cur={t.maoObra} prev={tAnt.maoObra}/>
                    </div>
                    <div className="dash2-hero-mini">
                        <div className="dash2-hero-mini-label">Ticket médio</div>
                        <div className="dash2-hero-mini-valor">{fmt(t.ticket, 'moeda')}</div>
                        <Delta cur={t.ticket} prev={tAnt.ticket}/>
                    </div>
                </div>
                <div className="dash2-hero-controls">
                    <div className="dash2-hero-controls-row">
                        <DateRangePicker
                            ranges={predefinedRanges}
                            value={dateRange}
                            onChange={setDateRange}
                            format="dd/MM/yyyy"
                            cleanable={false}
                            defaultCalendarValue={defaultDate}
                            style={{width: 240}}
                        />
                        <button onClick={() => loadData(dateRange, comparacao)} className="btn btn-sm btn-success">Aplicar</button>
                    </div>
                    <div className="dash2-compara">
                        <span className="dash2-compara-label">Comparar com:</span>
                        {COMPARACOES.map((c) => (
                            <button
                                key={c.key}
                                className={comparacao === c.key ? 'ativo' : ''}
                                onClick={() => trocarComparacao(c.key)}
                            >{c.label}</button>
                        ))}
                    </div>
                    {compLabel && <div className="dash2-compara-periodo">Comparando com {compLabel}</div>}
                </div>
            </header>

            <div className="dash2-kpis">
                {KPIS.map((kpi) => (
                    <div className="dash2-kpi" key={kpi.key}>
                        <div className="dash2-kpi-label">{kpi.label}</div>
                        <div className="dash2-kpi-value">{fmt(t[kpi.key], kpi.fmt)}</div>
                        <Delta cur={t[kpi.key]} prev={tAnt[kpi.key]} neutro={kpi.neutro}/>
                    </div>
                ))}
            </div>

            <Funil etapas={dados?.funil} onEtapa={abrirEtapaFunil}/>

            <div className="dash2-row dash2-row-main">
                <Card title="Vendas por mês" className="dash2-card-chart">
                    {grupo('Contratos por Mês') && <TimeSeriesChart group={grupo('Contratos por Mês')}/>}
                </Card>
                <Card title="Quem vendeu" className="dash2-card-table">
                    <table className="dash2-tabela">
                        <thead>
                            <tr>
                                <th>Vendedor</th>
                                <th className="num">Total</th>
                                <th className="num">Equip.</th>
                                <th className="num">Mão de obra</th>
                                <th className="num">Qtd</th>
                                <th className="num">%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vendedores.map((v, i) => (
                                <tr key={i}>
                                    <td>
                                        {v.id ? (
                                            <span className="dash2-link" onClick={() => abrirVendedor(v)}>
                                                <Avatar name={v.nome} size="sm" showName/>
                                            </span>
                                        ) : <Avatar name={v.nome} size="sm" showName/>}
                                    </td>
                                    <td className="num">{Util.moeda(Number(v.total) || 0)}</td>
                                    <td className="num">{Util.moeda(Number(v.equipamentos) || 0)}</td>
                                    <td className="num">{Util.moeda(Number(v.mao_obra) || 0)}</td>
                                    <td className="num">{v.quantidade}</td>
                                    <td className="num">{totalVend.total ? ((Number(v.total) / totalVend.total) * 100).toFixed(1) : 0}%</td>
                                </tr>
                            ))}
                            {vendedores.length > 0 && (
                                <tr className="destaque">
                                    <td>Total</td>
                                    <td className="num">{Util.moeda(totalVend.total)}</td>
                                    <td className="num">{Util.moeda(totalVend.equipamentos)}</td>
                                    <td className="num">{Util.moeda(totalVend.maoObra)}</td>
                                    <td className="num">{totalVend.quantidade}</td>
                                    <td className="num">100%</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Card>
            </div>

            <div className="dash2-row dash2-row-sec">
                <Card title="Placas por inversor" className="dash2-card-table">
                    <table className="dash2-tabela">
                        <thead>
                            <tr><th>Placas</th><th>Inversor</th><th className="num">Contratos</th></tr>
                        </thead>
                        <tbody>
                            {(dados?.placaInversor || []).map((row, i) => (
                                <tr key={i}>
                                    <td>{row.placas} placas</td>
                                    <td>{fmtInversor(row.inversor)}</td>
                                    <td className="num">{row.quantidade}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
                <Card title="Kits vendidos (com potência)" className="dash2-card-table">
                    {grupo('Kits Vendidos') && <TableChart group={grupo('Kits Vendidos')}/>}
                </Card>
                <Card title="Por cidade" className="dash2-card-table">
                    {grupo('Orçamentos e Contratos por Cidade') && <TableChart group={{...grupo('Orçamentos e Contratos por Cidade'), avatarColumns: [0]}}/>}
                </Card>
            </div>

            <div className="dash2-row dash2-row-2col">
                <Card title="Orçamentos e contratos por tag" className="dash2-card-table">
                    <table className="dash2-tabela">
                        <thead>
                            <tr><th>Origem</th><th className="num">Orçamentos</th><th className="num">Contratos</th></tr>
                        </thead>
                        <tbody>
                            {(dados?.porTag || []).map((row) => (
                                <tr key={row.tag} className={row.tag === '(Direto)' ? 'destaque' : ''}>
                                    <td>{row.tag === '(Direto)' ? 'Direto (sem tag)' : row.tag}</td>
                                    <td className="num">{row.orcamentos}</td>
                                    <td className="num">{row.contratos}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
                <Card title="Vendas por fornecedor" className="dash2-card-table">
                    <table className="dash2-tabela">
                        <thead>
                            <tr><th>Fornecedor</th><th className="num">Total</th><th className="num">Projetos</th></tr>
                        </thead>
                        <tbody>
                            {(dados?.fornecedores || []).map((row, i) => (
                                <tr key={i}>
                                    <td><Avatar name={row.nome} size="sm" showName /></td>
                                    <td className="num">{Util.moeda(Number(row.total) || 0)}</td>
                                    <td className="num">{row.quantidade}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            </div>

            <div className="dash2-row">
                <Card title="Orçamentos por dia" className="dash2-card-chart dash2-card-wide">
                    {grupo('Orçamentos por Dia') && <LineChart group={{...grupo('Orçamentos por Dia'), strokeWidth: 1.5}}/>}
                </Card>
            </div>

            <Overlay show={isLoading}/>
        </div>
    );
}
